import type { ChangeEvent } from 'react';
import { Loader2, Mic, RefreshCcw, Square } from "lucide-react";

import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useVoiceInput } from '@/hooks/useVoiceInput';

import type { QuestionRendererProps } from '../types';

export const LongTextQuestion = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  mode,
  disabled,
  sessionToken,
  ensureSessionToken,
  projectId,
  formId,
  version,
}: QuestionRendererProps<string>) => {
  if (question.type !== 'long_text') {
    return null;
  }

  const isInteractive = mode === 'runtime' && !disabled;
  const placeholder = question.placeholder || 'Share your thoughts...';
  const lengthRule = question.validation?.rules.find((rule) => rule.kind === 'length');
  const maxLength = lengthRule && lengthRule.kind === 'length' ? lengthRule.max : undefined;
  const minLength = lengthRule && lengthRule.kind === 'length' ? lengthRule.min : undefined;
  const characters = (value ?? '').length;
  const voiceInput = (
    question as { voiceInput?: { enabled?: boolean } }
  ).voiceInput;
  const voiceEnabled = voiceInput?.enabled ?? true;

  const { voiceState, micVisible, permissionMessage, startListening, stopListening, retryTranscription } =
    useVoiceInput({
      enabled: voiceEnabled,
      mode,
      disabled,
      questionRef: question.ref,
      questionType: "long_text",
      currentValue: value ?? "",
      locale: typeof navigator !== "undefined" ? navigator.language : "en-US",
      sessionToken,
      ensureSessionToken,
      projectId,
      formId,
      version,
      onTranscriptMerged: (nextValue) => {
        onChange?.(nextValue);
      },
    });

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    if (!isInteractive) {
      return;
    }
    onChange?.(event.target.value);
  };

  return (
    <div className="space-y-1.5">
      <Textarea
        value={value ?? ''}
        onChange={handleChange}
        onBlur={() => onBlur?.()}
        placeholder={placeholder}
        disabled={!isInteractive}
        maxLength={maxLength}
        minLength={minLength ?? undefined}
        rows={5}
        className="resize-y"
        aria-invalid={Boolean(error)}
        aria-describedby={`validation-${question.id}`}
        aria-errormessage={error ? `error-${question.id}` : undefined}
      />
      {micVisible ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={voiceState.state === "listening" ? stopListening : () => void startListening()}
            disabled={!isInteractive || voiceState.state === "processing"}
            aria-label={voiceState.state === "listening" ? "Stop voice recording" : "Start voice recording"}
          >
            {voiceState.state === "listening" ? (
              <>
                <Square className="h-3.5 w-3.5" />
                Stop recording
              </>
            ) : voiceState.state === "processing" ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Transcribing…
              </>
            ) : (
              <>
                <Mic className="h-3.5 w-3.5" />
                Answer by voice
              </>
            )}
          </Button>
          {voiceState.state === "error" && voiceState.canRetry ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => void retryTranscription()}
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          ) : null}
        </div>
      ) : null}
      {voiceState.state === "error" ? (
        <p className="text-xs text-amber-700">{voiceState.errorMessage}</p>
      ) : null}
      {permissionMessage ? (
        <p className="text-xs text-muted-foreground">{permissionMessage}</p>
      ) : null}
      {maxLength ? (
        <p className="text-xs text-muted-foreground">
          {characters} / {maxLength} characters
        </p>
      ) : null}
    </div>
  );
};
